import React, { useState, useCallback, useEffect, useContext } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
      EmbeddedCheckoutProvider,
      EmbeddedCheckout
} from '@stripe/react-stripe-js';
import { CSpinner, CButton } from '@coreui/react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Logger from '../utils/Logger';
import { getAuth } from 'firebase/auth';
import { AuthUserContext } from '../views/session';
import config from '../utils/EnvironmentSwitcher';
import { fetchWithFabricRetry } from '../utils/fabricReEnroll';
import { useNavigate } from 'react-router-dom';

const StripeCheckoutModal = ({ 
      show, 
      onHide, 
      clientSecret, 
      stripeAccountId, 
      artwork,
      items 
}) => {
      const [paymentStatus, setPaymentStatus] = useState(null);
      const [sessionDetails, setSessionDetails] = useState(null);
      const [isProcessing, setIsProcessing] = useState(false);
      const auth = getAuth();
      const { authUser, getValidToken } = useContext(AuthUserContext);
      const currentUser = auth.currentUser;
      const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
      const navigate = useNavigate();

      // Check if mobile device or small screen
      const isMobile = window.innerWidth <= 768;
      
      // Styles
      const modalStyle = {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: isMobile ? 'flex-start' : 'center',
            padding: isMobile ? '10px' : '20px',
            overflowY: 'auto', // Enable vertical scrolling
            WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
      };

      const contentStyle = {
            backgroundColor: 'white',
            padding: isMobile ? '15px' : '20px',
            borderRadius: '8px',
            width: isMobile ? '100%' : '80%',
            maxWidth: isMobile ? 'none' : '600px',
            minHeight: isMobile ? '100vh' : 'auto',
            maxHeight: isMobile ? 'none' : '90vh',
            overflowY: isMobile ? 'auto' : 'visible',
            position: 'relative',
            margin: isMobile ? '0' : 'auto'
      };

      // Function to verify payment status
      const verifyPaymentStatus = useCallback(async (sessionId) => {
            try {
                  setIsProcessing(true);
                  const idToken = await currentUser.getIdToken();
                  const response = await fetch(`${config.paymentUrl}/checkout-session-status`, {
                        method: 'POST',
                        headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${idToken}`
                        },
                        body: JSON.stringify({ sessionId })
                  });

                  if (!response.ok) {
                        throw new Error('Failed to verify payment status');
                  }

                  const data = await response.json();
                  setSessionDetails(data);
                  if (data.status === 'complete') {
                        const singleArtwork = artwork ?? items?.[0];
                        if (singleArtwork?.nftId || singleArtwork?.listingID) {
                              try {
                                    await handleNFTTransfer(sessionId, singleArtwork);
                                    setPaymentStatus('complete');
                              } catch (error) {
                                    Logger.info('NFT transfer failed:', error);
                                    setPaymentStatus('transfer_failed');
                                    toast.error('Payment successful but NFT transfer failed. Our team will assist you.');
                              }
                        } else {
                              setPaymentStatus('complete');
                              navigate(`/orders?session_id=${encodeURIComponent(sessionId)}`, { replace: true });
                              onHide();
                        }
                  } else {
                        setPaymentStatus(data.status);
                  }
            } catch (error) {
                  Logger.error('Error verifying payment:', error);
                  toast.error('Failed to verify payment status');
                  setPaymentStatus('error');
            } finally {
                  setIsProcessing(false);
            }
      }, [getValidToken, currentUser, artwork, items, navigate, onHide]);

      // Listen for Stripe events
      useEffect(() => {
            const handleMessage = async (event) => {
                  if (event.data?.type === 'stripe-embedded-checkout-complete') {
                        const sessionId = event.data.payload?.sessionId;
                        if (sessionId) {
                              await verifyPaymentStatus(sessionId);
                        }
                  }
            };

            window.addEventListener('message', handleMessage);
            return () => window.removeEventListener('message', handleMessage);
      }, [verifyPaymentStatus]);

      // Function to fetch client secret
      const fetchClientSecret = useCallback(() => {
            return Promise.resolve(clientSecret);
      }, [clientSecret]);

      const handleNFTTransfer = async (sessionId, art) => {
            const item = art ?? artwork ?? items?.[0];
            if (!item?.nftId) throw new Error('No artwork/item for NFT transfer');
            try {
                  const idToken = getValidToken ? await getValidToken() : (await currentUser?.getIdToken?.()) ?? null;
                  if (!idToken) throw new Error('Not authenticated');
                  const response = await fetchWithFabricRetry(`${config.apiUrl}/nft/transfer`, {
                        method: 'POST',
                        headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${idToken}`
                        },
                        body: JSON.stringify({
                              nftId: item.nftId,
                              listingId: item.listingID ?? item.listingId,
                              price: item.price ?? 0,
                              sellerId: item.sellerID ?? item.sellerId,
                              buyerId: authUser?.uid ?? currentUser?.uid,
                              sellerID: item.sellerID ?? item.sellerId,
                              sessionId: sessionId
                        })
                  });

                  if (!response.ok) {
                        const errorData = await response.json();
                        Logger.error('NFT transfer failed:', errorData);
                        throw new Error(errorData.message || 'Failed to transfer NFT');
                  }

                  const data = await response.json();
                  Logger.info('NFT transfer successful:', data);
                  toast.success('NFT transferred successfully!');
                  return data;

            } catch (error) {
                  Logger.error('Error in handleNFTTransfer:', error);
                  throw error; // Re-throw to be handled by the calling function
            }
      };

      // Updated renderPaymentStatus to include transfer status
      const renderPaymentStatus = () => {
            if (isProcessing) {
                  return (
                        <div className="text-center p-4">
                              <CSpinner color="primary" />
                              <p className="mt-3">Processing your purchase...</p>
                        </div>
                  );
            }

            if (paymentStatus === 'complete') {
                  return (
                        <div className="text-center p-4">
                              <div className="mb-4">
                                    <i className="fas fa-check-circle text-success fa-3x"></i>
                              </div>
                              <h4 className="mb-3">Purchase Successful!</h4>
                              <p>Your NFT has been transferred to your wallet.</p>
                              <p className="mb-2 mb-4">
                                    Transaction details have been sent to: {sessionDetails?.customer_email}
                              </p>
                              <CButton
                                    color="primary"
                                    onClick={() => {
                                          onHide();
                                          navigate('/marketplace');
                                    }}
                              >
                                    Return to Marketplace
                              </CButton>
                        </div>
                  );
            }

            if (paymentStatus === 'transfer_failed') {
                  return (
                        <div className="text-center p-4">
                              <div className="mb-4">
                                    <i className="fas fa-exclamation-triangle text-warning fa-3x"></i>
                              </div>
                              <h4 className="mb-3">Payment Successful - Transfer Pending</h4>
                              <p>Your payment was successful, but we encountered an issue transferring the NFT.</p>
                              <p>Our team has been notified and will complete the transfer shortly.</p>
                              <p className="mb-2 mb-4">
                                    Reference ID: {sessionDetails?.payment_intent}
                              </p>
                              <CButton
                                    color="primary"
                                    onClick={() => {
                                          onHide();
                                          navigate('/marketplace');
                                    }}
                              >
                                    Return to Marketplace
                              </CButton>
                        </div>
                  );
            }

            if (paymentStatus === 'error') {
                  return (
                        <div className="text-center p-4">
                              <div className="mb-4">
                                    <i className="fas fa-exclamation-circle text-danger fa-3x"></i>
                              </div>
                              <h4 className="mb-3">Payment Failed</h4>
                              <p>There was an issue processing your payment.</p>
                              <div className="mt-4">
                                    <CButton color="secondary" onClick={onHide}>Close</CButton>
                              </div>
                        </div>
                  );
            }

            return null;
      };

      // if (!show || !clientSecret || !stripeAccountId) {
      //       console.log('Missing required props:', { show, clientSecret, stripeAccountId });
      //       return null;
      // }

      return (
            <div style={modalStyle}>
                  <div style={contentStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                              <h3>Stripe Checkout</h3>
                              <button 
                                    onClick={onHide}
                                    style={{ 
                                          border: 'none', 
                                          background: 'none', 
                                          fontSize: '20px',
                                          cursor: 'pointer' 
                                    }}
                              >
                                    ×
                              </button>
                        </div>
                        {paymentStatus ? (
                              renderPaymentStatus()
                        ) : (
                              <div 
                                    id="checkout"
                                    style={{
                                          maxHeight: isMobile ? '70vh' : '500px',
                                          overflowY: 'auto',
                                          overflowX: 'hidden',
                                          WebkitOverflowScrolling: 'touch',
                                          border: isMobile ? '1px solid #e0e0e0' : 'none',
                                          borderRadius: '4px'
                                    }}
                              >
                                    <EmbeddedCheckoutProvider
                                          stripe={stripePromise}
                                          options={{ fetchClientSecret }}
                                    >
                                          <EmbeddedCheckout />
                                    </EmbeddedCheckoutProvider>
                              </div>
                        )}
                  </div>
            </div>
      );
};

export default StripeCheckoutModal;


